import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import FormPreviewModal from "@/components/FormPreviewModal";
import { Seo, breadcrumb } from "@/components/Seo";
import { Loader2, ArrowLeft, FileText, ShieldCheck, Package, ArrowRight, ShoppingBag, Gift, Eye } from "lucide-react";

export default function FormDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    setForm(null);
    api.get(`/forms/${code}`).then((r) => setForm(r.data)).catch(() => setForm(false));
  }, [code]);

  if (form === false) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-32 text-center">
        <h1 className="font-heading font-bold text-2xl">Form not found</h1>
        <Link to="/forms" className="text-primary font-semibold mt-4 inline-block">Back to the form library</Link>
      </div>
    );
  }
  if (!form) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-primary" /></div>;

  const addToPack = () => navigate("/forms", { state: { preselect: form.id } });

  return (
    <div className="max-w-[1080px] mx-auto px-6 lg:px-8 py-12">
      <Seo
        title={`${form.code} — ${form.title} | Centennial Forms & SOPs`}
        description={form.description || `${form.title} (${form.code}) — a ready-to-use ${form.system_name || "dealership"} form from Centennial Business Systems.`}
        canonicalPath={`/forms/${form.code}`}
        jsonLd={breadcrumb([
          { name: "Home", path: "/" },
          { name: "Forms", path: "/forms" },
          { name: form.title, path: `/forms/${form.code}` },
        ])}
      />
      <Link to="/forms" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-8">
        <ArrowLeft className="w-4 h-4" /> All forms
      </Link>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono text-xs bg-primary text-white px-2 py-1" data-testid="form-detail-code">{form.code}</span>
            <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{form.system_name}</span>
            {(form.updated_at || form.uploaded_at) && (
              <span className="text-[11px] text-muted-foreground" data-testid="form-detail-updated">
                Updated {new Date(form.updated_at || form.uploaded_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </span>
            )}
          </div>
          <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-tight" data-testid="form-detail-title">
            {form.title}
          </h1>
          <p className="text-muted-foreground mt-5 text-lg leading-relaxed">{form.description}</p>

          <div className="mt-8 border border-border bg-white p-6">
            <h2 className="font-heading font-semibold text-lg mb-4">About this form</h2>
            <dl className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><dt className="text-muted-foreground">Document code</dt><dd className="font-mono font-medium mt-0.5">{form.code}</dd></div>
              <div><dt className="text-muted-foreground">System</dt><dd className="font-medium mt-0.5">{form.system_name}</dd></div>
              <div><dt className="text-muted-foreground">Format</dt><dd className="font-medium mt-0.5">Editable, brandable PDF</dd></div>
              <div><dt className="text-muted-foreground">Protection</dt><dd className="font-medium mt-0.5">Licensed & watermarked</dd></div>
            </dl>
          </div>

          {form.related?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-heading font-semibold text-lg mb-4">More from {form.system_name}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {form.related.map((r) => (
                  <Link key={r.id} to={`/forms/${r.code}`} data-testid={`related-${r.code}`}
                    className="flex items-center gap-3 border border-border bg-white p-4 hover:border-primary btn-lift">
                    <FileText className="w-4 h-4 text-primary shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{r.title}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{r.code}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 border border-border bg-white p-6">
            <h3 className="font-heading font-bold text-lg">Get this form</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Bundle it into a 3-pack or 5-pack, or get it inside a full package.
            </p>
            <button onClick={addToPack} data-testid="form-detail-addpack"
              className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-primary text-white py-3.5 font-semibold btn-lift">
              <ShoppingBag className="w-4 h-4" /> Build a pack with this
            </button>
            <button onClick={() => setShowPreview(true)} data-testid="form-detail-preview"
              className="w-full mt-3 inline-flex items-center justify-center gap-2 border border-border py-3.5 font-semibold hover:border-primary">
              <Eye className="w-4 h-4" /> Preview this form
            </button>
            <Link to="/products" data-testid="form-detail-packages"
              className="w-full mt-3 inline-flex items-center justify-center gap-2 border border-border py-3.5 font-semibold hover:border-primary">
              <Package className="w-4 h-4" /> View full packages
            </Link>
            <div className="mt-6 pt-5 border-t border-border space-y-3 text-sm">
              {["Editable & brandable", "Personalized & license-protected", "Instant access in your portal"].map((t) => (
                <div key={t} className="flex items-center gap-2.5 text-foreground/80">
                  <ShieldCheck className="w-4 h-4 text-primary" /> {t}
                </div>
              ))}
            </div>
            <Link to="/forms" className="text-sm text-primary font-semibold inline-flex items-center gap-1 mt-5">
              <Gift className="w-4 h-4" /> New here? Claim 3 free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {showPreview && (
        <FormPreviewModal docId={form.id} title={form.title} code={form.code} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
}
